/**
 * Rotate / offset transforms for user markup geometry (PDF page space).
 */

import { DRAW_TOOLS } from './userMarkup.js';
import {
  buildCircularArcGeometry,
  circularArcEndpoints,
  isCircularArc,
  normalizeArcGeometry,
} from './arcGeometry.js';
import { getComponentPdfVertices, updateComponentGeometry } from './markupGeometry.js';
import { canvasToPdfPoint, pdfPointToCanvas } from './userMarkup.js';
import { realFeetToPdfPoints } from './drawMeasureFormat.js';

function rotationPivot(component) {
  const bbox = component?.geometry?.bbox || component?.bbox;
  if (Array.isArray(bbox) && bbox.length >= 4) {
    const [x, y, w, h] = bbox;
    return [x + w / 2, y + h / 2];
  }
  const verts = getComponentPdfVertices(component);
  if (!verts.length) return [0, 0];
  const sx = verts.reduce((s, [x]) => s + x, 0);
  const sy = verts.reduce((s, [, y]) => s + y, 0);
  return [sx / verts.length, sy / verts.length];
}

function rotatePdfPoint([x, y], cx, cy, cos, sin) {
  const dx = x - cx;
  const dy = y - cy;
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
}

function leftNormal([x0, y0], [x1, y1]) {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  return [-dy / len, dx / len];
}

function rightNormal(p0, p1) {
  const [nx, ny] = leftNormal(p0, p1);
  return [-nx, -ny];
}

function nearestSegmentOnPolyline(points, x, y, closed = false) {
  if (points.length < 2) return null;
  const segCount = closed ? points.length : points.length - 1;
  let best = null;
  let bestDist = Infinity;
  for (let i = 0; i < segCount; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lenSq = dx * dx + dy * dy;
    let t = lenSq ? ((x - a[0]) * dx + (y - a[1]) * dy) / lenSq : 0;
    t = Math.max(0, Math.min(1, t));
    const px = a[0] + t * dx;
    const py = a[1] + t * dy;
    const d = Math.hypot(x - px, y - py);
    if (d < bestDist) {
      bestDist = d;
      best = { index: i, t, point: [px, py], a, b };
    }
  }
  return best;
}

const OFFSET_ARROW_HIT_PX = 14;
const OFFSET_ARROW_PDF = 36;

/**
 * Two offset direction handles for hover UX (+left normal / −left normal in PDF space).
 * @returns {{ anchorCanvas: {x,y}, arrows: { side: number, x: number, y: number, angle: number }[] } | null}
 */
export function getOffsetArrowHandles(component, canvasX, canvasY, viewport) {
  if (!component || !viewport) return null;
  const geom = component.geometry || {};

  if (geom.type === 'circle' && geom.center && geom.radius != null) {
    const [cx, cy] = geom.center;
    const pdfPt = canvasToPdfPoint(canvasX, canvasY, viewport);
    const angle = Math.atan2(pdfPt[1] - cy, pdfPt[0] - cx);
    const anchorPdf = [
      cx + Math.cos(angle) * geom.radius,
      cy + Math.sin(angle) * geom.radius,
    ];
    const anchorCanvas = pdfPointToCanvas(anchorPdf[0], anchorPdf[1], viewport);
    const build = (side) => {
      const tipPdf = [
        anchorPdf[0] + Math.cos(angle) * side * OFFSET_ARROW_PDF,
        anchorPdf[1] + Math.sin(angle) * side * OFFSET_ARROW_PDF,
      ];
      const tip = pdfPointToCanvas(tipPdf[0], tipPdf[1], viewport);
      return {
        side,
        x: tip.x,
        y: tip.y,
        angle: Math.atan2(tip.y - anchorCanvas.y, tip.x - anchorCanvas.x),
      };
    };
    return { anchorCanvas, arrows: [build(1), build(-1)] };
  }

  const pdfVerts = getComponentPdfVertices(component);
  if (pdfVerts.length < 2) return null;

  const closed = geom.type === 'bbox'
    || (geom.type === 'polygon'
      && (geom.closed || component.drawTool === DRAW_TOOLS.FREEFORM));
  const pdfHit = nearestSegmentOnPolyline(pdfVerts, ...canvasToPdfPoint(canvasX, canvasY, viewport), closed);
  if (!pdfHit) return null;

  const { a, b, point: anchorPdf } = pdfHit;
  const [nx, ny] = leftNormal(a, b);
  const anchorCanvas = pdfPointToCanvas(anchorPdf[0], anchorPdf[1], viewport);

  const build = (side) => {
    const tipPdf = [
      anchorPdf[0] + nx * side * OFFSET_ARROW_PDF,
      anchorPdf[1] + ny * side * OFFSET_ARROW_PDF,
    ];
    const tip = pdfPointToCanvas(tipPdf[0], tipPdf[1], viewport);
    return {
      side,
      x: tip.x,
      y: tip.y,
      angle: Math.atan2(tip.y - anchorCanvas.y, tip.x - anchorCanvas.x),
    };
  };

  return { anchorCanvas, arrows: [build(1), build(-1)] };
}

export function hitTestOffsetArrow(canvasX, canvasY, handles) {
  if (!handles?.arrows?.length) return null;
  let best = null;
  let bestDist = OFFSET_ARROW_HIT_PX;
  for (const arrow of handles.arrows) {
    const d = Math.hypot(canvasX - arrow.x, canvasY - arrow.y);
    if (d <= bestDist) {
      bestDist = d;
      best = arrow;
    }
  }
  return best;
}

export function hoverOffsetArrowSide(canvasX, canvasY, handles) {
  const hit = hitTestOffsetArrow(canvasX, canvasY, handles);
  return hit?.side ?? null;
}

function offsetOpenPolyline(points, distance) {
  if (points.length < 2) return points;
  if (points.length === 2) {
    const [nx, ny] = leftNormal(points[0], points[1]);
    return points.map(([x, y]) => [x + nx * distance, y + ny * distance]);
  }
  return points.map((pt, i) => {
    let nx = 0;
    let ny = 0;
    if (i > 0) {
      const n = leftNormal(points[i - 1], points[i]);
      nx += n[0];
      ny += n[1];
    }
    if (i < points.length - 1) {
      const n = leftNormal(points[i], points[i + 1]);
      nx += n[0];
      ny += n[1];
    }
    const len = Math.hypot(nx, ny) || 1;
    return [pt[0] + (nx / len) * distance, pt[1] + (ny / len) * distance];
  });
}

function offsetClosedPolyline(points, distance) {
  if (points.length < 3) return offsetOpenPolyline(points, distance);
  const extended = [...points, points[0]];
  const offset = offsetOpenPolyline(extended, distance);
  return offset.slice(0, points.length);
}

export function componentSupportsOffset(component) {
  const geom = component?.geometry;
  if (!geom) return false;
  if (geom.type === 'circle') return true;
  if (geom.type === 'arc') return isCircularArc(normalizeArcGeometry(geom));
  if (geom.type === 'bbox') return true;
  const verts = getComponentPdfVertices(component);
  return verts.length >= 2;
}

export function rotateComponent(component, degrees, { scaleRatio = null, pivot = null } = {}) {
  if (!component || !degrees) return component;
  // Positive degrees = clockwise (canvas/CAD drag); PDF rotation matrix is CCW-positive.
  const rad = (-degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const [cx, cy] = pivot || rotationPivot(component);
  const geom = component.geometry || {};

  if (geom.type === 'circle' && geom.center) {
    const newCenter = rotatePdfPoint(geom.center, cx, cy, cos, sin);
    return updateComponentGeometry(
      component,
      [newCenter, [newCenter[0] + geom.radius, newCenter[1]]],
      { scaleRatio },
    );
  }

  if (geom.type === 'arc') {
    const normalized = normalizeArcGeometry(geom);
    if (isCircularArc(normalized)) {
      const newCenter = rotatePdfPoint(normalized.center, cx, cy, cos, sin);
      const startAngle = normalized.startAngle + rad;
      const endAngle = normalized.endAngle + rad;
      const nextGeometry = {
        ...normalized,
        center: newCenter,
        startAngle,
        endAngle,
        points: circularArcEndpoints({
          center: newCenter,
          radius: normalized.radius,
          startAngle,
          endAngle,
        }),
      };
      return updateComponentGeometry(
        component,
        getComponentPdfVertices({ geometry: nextGeometry }),
        { scaleRatio },
      );
    }
    const pts = [
      ...(normalized.points || []),
      normalized.bulgePoint,
    ].filter(Boolean);
    const rotated = pts.map((p) => rotatePdfPoint(p, cx, cy, cos, sin));
    return updateComponentGeometry(component, rotated, { scaleRatio });
  }

  const verts = getComponentPdfVertices(component);
  if (verts.length < 2) return component;
  const rotated = verts.map((p) => rotatePdfPoint(p, cx, cy, cos, sin));
  return updateComponentGeometry(component, rotated, { scaleRatio });
}

export function offsetComponent(component, distancePdf, { scaleRatio = null } = {}) {
  if (!component || !distancePdf) return component;
  const geom = component.geometry || {};

  if (geom.type === 'circle' && geom.center && geom.radius != null) {
    const newRadius = Math.max(1, geom.radius + distancePdf);
    return updateComponentGeometry(
      component,
      [geom.center, [geom.center[0] + newRadius, geom.center[1]]],
      { scaleRatio },
    );
  }

  if (geom.type === 'arc') {
    const normalized = normalizeArcGeometry(geom);
    if (isCircularArc(normalized)) {
      const newRadius = Math.max(1, normalized.radius + distancePdf);
      const next = buildCircularArcGeometry(
        normalized.center,
        [
          normalized.center[0] + newRadius * Math.cos(normalized.startAngle),
          normalized.center[1] + newRadius * Math.sin(normalized.startAngle),
        ],
        [
          normalized.center[0] + newRadius * Math.cos(normalized.endAngle),
          normalized.center[1] + newRadius * Math.sin(normalized.endAngle),
        ],
      );
      return updateComponentGeometry(
        component,
        getComponentPdfVertices({ geometry: next }),
        { scaleRatio },
      );
    }
    return component;
  }

  const closed = geom.type === 'polygon'
    && (geom.closed || component.drawTool === DRAW_TOOLS.FREEFORM || geom.type === 'bbox');
  const verts = getComponentPdfVertices(component);
  if (verts.length < 2) return component;

  const offsetVerts = closed
    ? offsetClosedPolyline(verts, distancePdf)
    : offsetOpenPolyline(verts, distancePdf);

  return updateComponentGeometry(component, offsetVerts, { scaleRatio });
}

function dedupePdfPoints(points, epsilon = 0.5) {
  const out = [];
  for (const p of points) {
    if (!out.some((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) < epsilon)) out.push(p);
  }
  return out;
}

function segmentMirrorLineIntersection(a, b, lineP, lineQ) {
  const dx1 = b[0] - a[0];
  const dy1 = b[1] - a[1];
  const dx2 = lineQ[0] - lineP[0];
  const dy2 = lineQ[1] - lineP[1];
  const denom = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((lineP[0] - a[0]) * dy2 - (lineP[1] - a[1]) * dx2) / denom;
  if (t < -1e-9 || t > 1 + 1e-9) return null;
  return [a[0] + t * dx1, a[1] + t * dy1];
}

function insertPolylineMirrorIntersections(points, axisA, axisB, closed = false) {
  if (points.length < 2) return points;
  const out = [];
  const n = points.length;
  const segCount = closed ? n : n - 1;
  for (let i = 0; i < segCount; i += 1) {
    const p0 = points[i];
    const p1 = points[(i + 1) % n];
    out.push(p0);
    const hit = segmentMirrorLineIntersection(p0, p1, axisA, axisB);
    if (hit) out.push(hit);
  }
  if (!closed) out.push(points[n - 1]);
  return dedupePdfPoints(out);
}

export function mirrorPdfPoint(point, axisA, axisB) {
  const [px, py] = point;
  const [ax, ay] = axisA;
  const [bx, by] = axisB;
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy);
  if (len < 1e-12) return point;
  const nx = -dy / len;
  const ny = dx / len;
  const d = (px - ax) * nx + (py - ay) * ny;
  return [px - 2 * d * nx, py - 2 * d * ny];
}

export function mirrorComponent(component, axisA, axisB, {
  scaleRatio = null,
  splitPolyline = true,
} = {}) {
  if (!component || !axisA || !axisB) return component;
  const geom = component.geometry || {};
  let verts = getComponentPdfVertices(component);
  if (verts.length < 1) return component;

  const closed = geom.type === 'polygon'
    || geom.type === 'bbox'
    || (geom.closed && geom.type !== 'polyline');
  const isOpenChain = geom.type === 'polyline' || geom.type === 'line'
    || (geom.type === 'polygon' && !geom.closed);

  if (splitPolyline && verts.length >= 2) {
    if (isOpenChain) {
      verts = insertPolylineMirrorIntersections(verts, axisA, axisB, false);
    } else if (closed) {
      verts = insertPolylineMirrorIntersections(verts, axisA, axisB, true);
    }
  }

  if (geom.type === 'arc') {
    const normalized = normalizeArcGeometry(geom);
    if (isCircularArc(normalized)) {
      const newCenter = mirrorPdfPoint(normalized.center, axisA, axisB);
      const lineAngle = Math.atan2(axisB[1] - axisA[1], axisB[0] - axisA[0]);
      const mirrorAngle = (a) => 2 * lineAngle - a;
      const startAngle = mirrorAngle(normalized.startAngle);
      const endAngle = mirrorAngle(normalized.endAngle);
      const nextGeometry = {
        ...normalized,
        center: newCenter,
        startAngle,
        endAngle,
        points: circularArcEndpoints({
          center: newCenter,
          radius: normalized.radius,
          startAngle,
          endAngle,
        }),
      };
      return updateComponentGeometry(
        component,
        getComponentPdfVertices({ geometry: nextGeometry }),
        { scaleRatio },
      );
    }
    const pts = [
      ...(normalized.points || []),
      normalized.bulgePoint,
    ].filter(Boolean);
    const mirrored = pts.map((p) => mirrorPdfPoint(p, axisA, axisB));
    return updateComponentGeometry(component, mirrored, { scaleRatio });
  }

  const mirrored = verts.map((p) => mirrorPdfPoint(p, axisA, axisB));
  return updateComponentGeometry(component, mirrored, { scaleRatio });
}

/** Parse offset distance from user input (feet when scale available, else PDF points). */
export function parseOffsetDistanceInput(text, scaleRatio) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return null;
  if (scaleRatio?.numerator && scaleRatio?.denominator) {
    return realFeetToPdfPoints(n, scaleRatio);
  }
  return n;
}
